import { createContext, useContext, useEffect, useMemo, useCallback, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { dict, type Lang } from './dict';

type Ctx = {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: string) => any;
  // Helper to build a localized path: lp('/sobre') => '/sobre' or '/en/sobre'
  lp: (path: string) => string;
};

const LanguageCtx = createContext<Ctx | null>(null);

function lookup(obj: any, parts: string[]): any {
  let cur = obj;
  for (const p of parts) {
    if (cur == null) return undefined;
    cur = cur[p];
  }
  return cur;
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();

  const isEnPath =
    location.pathname === '/en' || location.pathname.startsWith('/en/');
  const lang: Lang = isEnPath ? 'en' : 'pt';

  // Auto-detect on first visit (no saved preference).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const saved = localStorage.getItem('criaaa-lang');
      if (saved) return;
      const browser = (navigator.language || '').toLowerCase();
      const wantsEn = browser.startsWith('en');
      if (wantsEn && !isEnPath) {
        localStorage.setItem('criaaa-lang', 'en');
        navigate('/en' + location.pathname + location.search + location.hash, { replace: true });
      } else {
        localStorage.setItem('criaaa-lang', wantsEn ? 'en' : 'pt');
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setLang = useCallback(
    (l: Lang) => {
      try { localStorage.setItem('criaaa-lang', l); } catch {}
      const stripped = isEnPath
        ? location.pathname.replace(/^\/en/, '') || '/'
        : location.pathname;
      const target =
        l === 'en'
          ? '/en' + (stripped === '/' ? '' : stripped)
          : stripped;
      navigate(target + location.search + location.hash);
    },
    [isEnPath, location.pathname, location.search, location.hash, navigate]
  );

  const t = useCallback(
    (key: string) => {
      const parts = key.split('.');
      const v = lookup((dict as any)[lang], parts);
      if (v !== undefined) return v;
      const fb = lookup((dict as any).pt, parts);
      return fb !== undefined ? fb : key;
    },
    [lang]
  );

  const lp = useCallback(
    (path: string) => {
      if (!path.startsWith('/')) path = '/' + path;
      return lang === 'en' ? '/en' + (path === '/' ? '' : path) : path;
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t, lp }), [lang, setLang, t, lp]);

  // Reflect language in <html lang="...">
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang === 'en' ? 'en' : 'pt-BR';
    }
  }, [lang]);

  return <LanguageCtx.Provider value={value}>{children}</LanguageCtx.Provider>;
}

export function useLang(): Ctx {
  const ctx = useContext(LanguageCtx);
  if (!ctx) {
    // Fallback so components rendered outside provider (tests, edge cases) don't crash.
    return {
      lang: 'pt',
      setLang: () => {},
      t: (k: string) => {
        const v = lookup((dict as any).pt, k.split('.'));
        return v !== undefined ? v : k;
      },
      lp: (p: string) => p,
    };
  }
  return ctx;
}
